import { useRef, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EWidgetType,
  Flex,
  Text,
  type IChatMessage,
  type IChatMessageWidget,
} from '@kognitos/lattice';
import { Sentry } from '@/modules/sentry';
import type {
  IChatMessageWithWidgetData,
  IIdpPreviewWidgetData,
  IGuideEntryWidgetData,
  IFollowUpWidgetData,
} from '@/shared/hooks/useRunChat';
import { FollowUpSuggestionsCard } from '../components/exception/FollowUpSuggestionsCard';
import { IDPPreviewRenderer } from '@/modules/outputs/components/renderers/IDPPreviewRenderer';
import { ERenderMode } from '@/modules/outputs/types';
import {
  EIDPSource,
  type IIDPFactInputData,
  type IIDPMissingField,
} from '@/modules/idp/types';
import {
  extractFileIdFromSource,
  isFileResourceName,
} from '@/shared/utils/file-utils';
import type { IParsedGuideEntry } from '@/shared/utils/chat-utils';

const InlineGuideEntryWidget = lazy(() =>
  import('../components/run-assistant/InlineGuideEntryWidget').then(m => ({
    default: m.default,
  })),
);

const GuideEntryApprovalPills = lazy(() =>
  import('../components/run-assistant/GuideEntryApprovalPills').then(m => ({
    default: m.default,
  })),
);

/**
 * Guide entry action handlers interface.
 */
interface IGuideEntryActions {
  /** Handler for accepting a guide entry */
  onAccept?: (guideEntry: IParsedGuideEntry) => Promise<void> | void;
  /** Handler for rejecting a guide entry */
  onReject?: (guideEntry: IParsedGuideEntry) => Promise<void> | void;
  /** Handler for archiving a guide entry */
  onArchive?: (guideEntry: IParsedGuideEntry) => Promise<void> | void;
  /** Handler for editing a guide entry (focuses chat input) */
  onEdit?: (guideEntry: IParsedGuideEntry) => void;
  /** Whether guide entry actions are loading */
  isLoading?: boolean;
}

/**
 * Options for the useProcessChatMessages hook.
 */
interface IUseProcessChatMessagesOptions {
  /** Raw messages from the chat (with widget data) */
  messages: IChatMessage[];
  /** Transformed exception fields for IDP preview */
  exceptionFields: IIDPMissingField[];
  /** Callback when IDP preview is clicked, with the widget's specific idpData and read-only flag */
  onPreviewClick?: (
    idpData?: IIDPFactInputData | null,
    readOnly?: boolean,
  ) => void;
  /** Guide entry action handlers (optional - only needed if guide entries should be actionable) */
  guideEntryActions?: IGuideEntryActions;
  /** Whether the component is in read-only mode (affects IDP preview) */
  readOnly?: boolean;
  /** Whether to filter out outputs widgets (used in QuestionCard) */
  filterOutputsWidgets?: boolean;
  /** Callback when a suggested prompt button is clicked (for button-group widgets) */
  onSuggestedPromptClick?: (prompt: string) => void;
  /** Callback when a follow-up suggestion is clicked (sends its message to the agent) */
  onFollowUpClick?: (message: string) => void;
  /** Whether a message is being sent (disables follow-up suggestions) */
  isFollowUpSubmitting?: boolean;
}

/**
 * Deps that affect per-message processing, passed through to avoid closing over outer scope.
 */
interface IProcessDeps {
  exceptionFields: IIDPMissingField[];
  onPreviewClick?: IUseProcessChatMessagesOptions['onPreviewClick'];
  guideEntryActions?: IGuideEntryActions;
  readOnly: boolean;
  onSuggestedPromptClick?: (prompt: string) => void;
  onFollowUpClick?: (message: string) => void;
  isFollowUpSubmitting?: boolean;
  t: (key: string) => string;
}

/**
 * Processes a single message, transforming widget data into renderable widgets.
 *
 * @param message - The message to process
 * @param allMessages - The full filtered messages array (needed for idp-preview lookahead)
 * @param index - Index of this message in allMessages
 * @param deps - Processing dependencies
 * @returns Processed message with widget rendered
 */
function processSingleMessage(
  message: IChatMessage,
  allMessages: IChatMessage[],
  index: number,
  deps: IProcessDeps,
): IChatMessage {
  const widget = message.widget as IChatMessageWidget | undefined;
  if (
    widget?.type === EWidgetType.BUTTON_GROUP &&
    widget.buttons?.length &&
    deps.onSuggestedPromptClick
  ) {
    const onPromptClick = deps.onSuggestedPromptClick;
    return {
      ...message,
      widget: {
        ...widget,
        buttons: widget.buttons.map(btn => ({
          ...btn,
          onClick: btn.onClick ?? (() => onPromptClick(btn.label)),
        })),
      },
    } as IChatMessage;
  }

  const msgWithWidget = message as IChatMessageWithWidgetData;

  if (msgWithWidget.widgetData?.type === 'idp-preview') {
    const widgetData = msgWithWidget.widgetData as IIdpPreviewWidgetData;

    // MAN-3747: the fields-only "Provide missing values" card (and its dialog)
    // was removed from the design. Only document-backed previews remain; the
    // fields-only case (no source) is dropped in the filter step below.
    if (!(widgetData.source && isFileResourceName(widgetData.source))) {
      return message;
    }

    const nextMsg = allMessages[index + 1] as
      | IChatMessageWithWidgetData
      | undefined;
    const isRespondedTo = nextMsg?.widgetData?.type === 'fact-edit';
    const fileId = extractFileIdFromSource(widgetData.source);
    return {
      ...message,
      widget: {
        type: EWidgetType.CUSTOM,
        customContent: (
          <IDPPreviewRenderer
            documentName={fileId}
            fields={[]}
            exceptionFields={deps.exceptionFields}
            source={EIDPSource.EXCEPTION}
            mode={ERenderMode.PREVIEW}
            onPreviewClick={() =>
              deps.onPreviewClick?.(widgetData.idpData, isRespondedTo)
            }
            sourceCount={widgetData.sourceCount || 1}
          />
        ),
      },
    };
  }

  if (
    msgWithWidget.widgetData?.type === 'guide-entry' &&
    deps.guideEntryActions
  ) {
    const widgetData = msgWithWidget.widgetData as IGuideEntryWidgetData;
    return {
      ...message,
      widget: {
        type: EWidgetType.CUSTOM,
        customContent: (
          <Sentry.ErrorBoundary
            fallback={
              <Text color="muted">
                {deps.t('common.error.widgetLoadFailed')}
              </Text>
            }
          >
            <Suspense fallback={null}>
              <Flex direction="column" gap={3} className="w-full">
                {/* Display-only card; approve/reject live in the pills below. */}
                <InlineGuideEntryWidget guideEntry={widgetData.guideEntry} />
                <GuideEntryApprovalPills
                  guideEntry={widgetData.guideEntry}
                  onApprove={deps.guideEntryActions.onAccept}
                  onReject={deps.guideEntryActions.onReject}
                  isLoading={deps.guideEntryActions.isLoading}
                />
              </Flex>
            </Suspense>
          </Sentry.ErrorBoundary>
        ),
      },
    };
  }

  if (msgWithWidget.widgetData?.type === 'follow-up') {
    const widgetData = msgWithWidget.widgetData as IFollowUpWidgetData;
    return {
      ...message,
      widget: {
        type: EWidgetType.CUSTOM,
        customContent: (
          <Sentry.ErrorBoundary
            fallback={
              <Text color="muted">
                {deps.t('common.error.widgetLoadFailed')}
              </Text>
            }
          >
            <FollowUpSuggestionsCard
              suggestions={widgetData.suggestions}
              onSend={deps.onFollowUpClick}
              isSubmitting={deps.isFollowUpSubmitting}
            />
          </Sentry.ErrorBoundary>
        ),
      },
    };
  }

  return message;
}

/**
 * Checks whether a message at `index` is an idp-preview that depends on
 * the next message (lookahead for "isRespondedTo" detection).
 * When a new message is inserted after such a message, it must be reprocessed.
 */
function isIdpPreviewWithLookahead(message: IChatMessage): boolean {
  return (
    (message as IChatMessageWithWidgetData).widgetData?.type === 'idp-preview'
  );
}

/**
 * A fields-only "Provide missing values" idp-preview (no document source).
 * These cards/dialogs were removed from the design (MAN-3747); document-backed
 * previews are identified by a file resource `source`.
 */
function isFieldsOnlyIdpPreview(message: IChatMessage): boolean {
  const widgetData = (message as IChatMessageWithWidgetData).widgetData;
  if (widgetData?.type !== 'idp-preview') return false;
  const { source } = widgetData as IIdpPreviewWidgetData;
  return !(source && isFileResourceName(source));
}

/**
 * Detects the append-only case by comparing message arrays by object reference.
 * During streaming, the Zustand store creates `[...existingMessages, newMessage]`,
 * so existing message objects keep their identity.
 *
 * @returns The index where new messages start, or -1 if the change is not append-only
 */
function findAppendStart(
  current: IChatMessage[],
  previous: IChatMessage[],
): number {
  if (current.length < previous.length) return -1;

  for (let i = 0; i < previous.length; i++) {
    if (current[i] !== previous[i]) return -1;
  }

  return previous.length;
}

/**
 * Hook that processes chat messages and transforms widget data into renderable widgets.
 *
 * Uses incremental processing to avoid O(n) work on every streaming event.
 * When messages are only appended (the common streaming case), only the new
 * messages are processed and the previous results are reused.
 *
 * Handles transformation of:
 * - idp-preview widgets → IDPPreviewRenderer (document-backed previews only)
 * - guide-entry widgets → InlineGuideEntryWidget
 * - button-group widgets → onClick injection for suggested prompts
 *
 * @param options - Hook options
 * @returns Processed messages with widgets rendered as React components
 */
export function useProcessChatMessages({
  messages,
  exceptionFields,
  onPreviewClick,
  guideEntryActions,
  readOnly = false,
  filterOutputsWidgets = false,
  onSuggestedPromptClick,
  onFollowUpClick,
  isFollowUpSubmitting,
}: IUseProcessChatMessagesOptions): IChatMessage[] {
  const { t } = useTranslation('common');

  // Incremental processing cache stored in a ref.
  //
  // Trade-off: writing to cacheRef during render is a render-phase side effect.
  // This is intentional — the alternatives are worse for our streaming hot path:
  //
  // - useMemo + ref: an abandoned concurrent render can write to the ref while
  //   React discards the memoized slot. The next committed render consults the
  //   stale ref and incorrectly skips reprocessing.
  //
  // - useEffect for cache writes: fully safe under concurrent mode, but doubles
  //   render work (first render always recomputes, effect stores cache, next
  //   render reads it). During streaming where messages arrive every ~16ms,
  //   the extra render per message is measurable.
  //
  // Current approach (inline ref write): comparison logic runs every render, so
  // an abandoned render that corrupts the ref is detected on the next render
  // via referential mismatch, triggering a full reprocess. Correctness is
  // preserved because results are returned from stack-local variables, not
  // re-read from the ref after a yield.
  //
  // Known limitation: if two concurrent renders interleave (render A writes
  // cache, yields, render B overwrites it, render A resumes), the cache may
  // briefly hold data from a non-committed render. The next render detects
  // the mismatch and does one extra full reprocess. This is a performance
  // miss (one wasted incremental optimization), not a correctness bug.
  const cacheRef = useRef<{
    messages: IChatMessage[];
    filterOutputsWidgets: boolean;
    filteredMessages: IChatMessage[];
    processedMessages: IChatMessage[];
    exceptionFields: IIDPMissingField[];
    onPreviewClick: IUseProcessChatMessagesOptions['onPreviewClick'];
    guideEntryActions: IGuideEntryActions | undefined;
    readOnly: boolean;
    onSuggestedPromptClick: ((prompt: string) => void) | undefined;
    onFollowUpClick: ((message: string) => void) | undefined;
    isFollowUpSubmitting: boolean | undefined;
    t: (key: string) => string;
  } | null>(null);

  const cache = cacheRef.current;

  // Fast path: every input is referentially identical — return cached result
  if (
    cache !== null &&
    cache.messages === messages &&
    cache.filterOutputsWidgets === filterOutputsWidgets &&
    cache.exceptionFields === exceptionFields &&
    cache.onPreviewClick === onPreviewClick &&
    cache.guideEntryActions === guideEntryActions &&
    cache.readOnly === readOnly &&
    cache.onSuggestedPromptClick === onSuggestedPromptClick &&
    cache.onFollowUpClick === onFollowUpClick &&
    cache.isFollowUpSubmitting === isFollowUpSubmitting &&
    cache.t === t
  ) {
    return cache.processedMessages;
  }

  const filteredMessages = messages.filter(message => {
    const msgWithWidget = message as IChatMessageWithWidgetData;
    if (filterOutputsWidgets && msgWithWidget.widgetData?.type === 'outputs') {
      return false;
    }
    // MAN-3747: drop widget-only fields-only preview messages so the removed
    // "Provide missing values" card leaves no empty chat bubble behind.
    if (isFieldsOnlyIdpPreview(message) && !message.content?.trim()) {
      return false;
    }
    return true;
  });

  const deps: IProcessDeps = {
    exceptionFields,
    onPreviewClick,
    guideEntryActions,
    readOnly,
    onSuggestedPromptClick,
    onFollowUpClick,
    isFollowUpSubmitting,
    t,
  };

  const updateCache = (filtered: IChatMessage[], processed: IChatMessage[]) => {
    cacheRef.current = {
      messages,
      filterOutputsWidgets,
      filteredMessages: filtered,
      processedMessages: processed,
      exceptionFields,
      onPreviewClick,
      guideEntryActions,
      readOnly,
      onSuggestedPromptClick,
      onFollowUpClick,
      isFollowUpSubmitting,
      t,
    };
  };

  // Check if non-message deps changed (requires full reprocess)
  const depsUnchanged =
    cache !== null &&
    cache.exceptionFields === exceptionFields &&
    cache.onPreviewClick === onPreviewClick &&
    cache.guideEntryActions === guideEntryActions &&
    cache.readOnly === readOnly &&
    cache.onSuggestedPromptClick === onSuggestedPromptClick &&
    cache.onFollowUpClick === onFollowUpClick &&
    cache.isFollowUpSubmitting === isFollowUpSubmitting &&
    cache.t === t;

  if (depsUnchanged) {
    const appendStart = findAppendStart(
      filteredMessages,
      cache.filteredMessages,
    );

    if (appendStart >= 0) {
      // No new messages — return cached result, updating input refs for fast path
      if (appendStart === filteredMessages.length) {
        updateCache(filteredMessages, cache.processedMessages);
        return cache.processedMessages;
      }

      // Append-only: process only the new messages
      const newProcessed = filteredMessages
        .slice(appendStart)
        .map((msg, i) =>
          processSingleMessage(msg, filteredMessages, appendStart + i, deps),
        );

      let prevProcessed = cache.processedMessages;

      // If the last previously-processed message is an idp-preview,
      // reprocess it because its "isRespondedTo" depends on the next message
      // which may now be the first newly-appended message.
      if (
        appendStart > 0 &&
        isIdpPreviewWithLookahead(filteredMessages[appendStart - 1])
      ) {
        const reprocessed = processSingleMessage(
          filteredMessages[appendStart - 1],
          filteredMessages,
          appendStart - 1,
          deps,
        );
        prevProcessed = [...prevProcessed.slice(0, -1), reprocessed];
      }

      const result = [...prevProcessed, ...newProcessed];
      updateCache(filteredMessages, result);
      return result;
    }
  }

  // Full reprocess: first render, deps changed, or messages were modified/reordered
  const result = filteredMessages.map((msg, i) =>
    processSingleMessage(msg, filteredMessages, i, deps),
  );

  updateCache(filteredMessages, result);
  return result;
}

export type { IGuideEntryActions, IUseProcessChatMessagesOptions };
